import { elementUpdateAttribute } from "@/utils/html";
import {
  isFunctionalComponent,
  isHTMLElement,
  isHTMLString,
  isSVGElement,
} from "@/utils/is";
import { isFunction, isString } from "math";
import { fetchContent, getRawFromModule, getSVGTree } from ".";
import { isVNode, type CSSProperties, type Ref, type VNode } from "vue";
import { isSVGIconObject, isSVGIconObjectArray } from "@/types/elements/icon";
import Icon from "..";
import type { IIConProps } from "../types";
import { isSVGPathCommandArray } from "@/utils/svg/types";
import { svgCommandPath } from "@/utils/svg";
import { unwrapElementRef } from "@/utils/dom/unwrap";

const onMounted = (node: VNode, props: IIConProps) => {
  queueMicrotask(() => {
    if (!node.el) return;
    const el = node.el as SVGElement;
    if (!el) return;
    if (!isFunction(el.getAttribute)) return;
    if (!isFunction(el.setAttribute)) return;
    elementUpdateAttribute(
      el as unknown as HTMLElement,
      "stroke",
      props.stroke || "none",
    );
    if (el.getAttribute("width") !== el.getAttribute("height")) return;
    if (props.size) {
      el.setAttribute("width", props.size + "");
      el.setAttribute("height", props.size + "");
    }
    if (
      !el.querySelector("g") ||
      !el.querySelector("defs") ||
      (el as unknown as HTMLElement).querySelectorAll("path").length < 2
    )
      return;
    const svgPath = el.querySelector("path");
    if (
      svgPath &&
      svgPath.parentElement &&
      svgPath.parentElement.parentElement
    ) {
      const upper = svgPath.parentElement?.parentElement;
      upper.innerHTML = "";
      upper.appendChild(svgPath);
    }
  });
};

export const convertIconSource = async (args: {
  source: any;
  style: Ref<CSSProperties>;
  flipStyle: Ref<CSSProperties>;
  depth: Ref<number>;
  props: IIConProps;
}): Promise<any> => {
  try {
    const { source, style, flipStyle, depth, props } = args;
    const s = source as any;

    if (isSVGPathCommandArray(s)) {
      return (
        <svg width={props.width} height={props.height}>
          {svgCommandPath({
            commands: s,
            ...props,
          })}
        </svg>
      );
    }

    if (isSVGIconObjectArray(s)) {
      return (
        <div class="relative">
          {s.map((it) => {
            return (
              <Icon
                {...it}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              />
            );
          })}
        </div>
      );
    }

    if (typeof s === "object" && isFunction(s.render)) {
      const out = s.render({});
      if (out) return out;
    }

    if (isString(s)) {
      if (s.startsWith("/") && s.includes(".svg")) return await fetchContent(s);
      if (s.startsWith("@")) return getRawFromModule(s);

      if (s.startsWith("data:image/svg+xml,")) {
        const part = s.split("data:image/svg+xml,")[1];
        const contents = decodeURI(part);
        if (isHTMLString(contents)) {
          const tree = getSVGTree(contents);
          if (tree) {
            return tree;
          }
        }
      }
      if (isHTMLString(s)) {
        const tree = getSVGTree(s);
        if (tree) {
          return tree;
        }
        return <div />;
      }

      return <img src={s} height={props.height} width={props.width} style={[style.value, flipStyle.value]} />;
    }

    if (isSVGIconObject(s)) {
      return (
        <Icon
          fill={props.fill}
          stroke={props.stroke}
          {...s}
          src={s.src}
          depth={depth.value + 1}
          key={`icon-${depth.value}`}
        />
      );
    }

    if (isSVGElement(s) || isHTMLElement(s)) {
      return s;
    }

    if (isFunctionalComponent(s)) {
      const Comp = s as any;
      return (
        <Comp
          onVnodeMounted={(node: VNode) => onMounted(node, props)}
          style={[style.value]}
        />
      );
    }

    if (isVNode(s)) {
      const el = unwrapElementRef(s);
      if (el && isSVGElement(el)) {
        return convertIconSource({ ...args, source: el });
      }

      return s;
    }

    return <div />;
  } catch (e) {
    console.error(e);
    return <span />;
  }
};
